import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Category } from '../../types';

interface CategoryCardProps {
  category: Category;
}

export const CategoryCard: React.FC<CategoryCardProps> = ({ category }) => {
  const getIconComponent = (iconName?: string) => {
    // For now, using emoji icons. In a real app, you might use a proper icon library
    const iconMap: Record<string, string> = {
      home: '🏠',
      building: '🏢',
      'shopping-bag': '🛍️',
      factory: '🏭',
      apartment: '🏬',
      villa: '🏡',
      office: '🏢',
      warehouse: '📦',
      retail: '🛒',
      industrial: '⚙️',
    };
    
    return iconMap[iconName || 'building'] || '🏢';
  };

  return (
    <Link
      to={`/category/${category.slug}`}
      className="group bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden card-hover"
    >
      {/* Header with color accent */}
      <div 
        className="h-2 w-full"
        style={{ backgroundColor: category.color }}
      ></div>
      
      <div className="p-6">
        {/* Icon */}
        <div className="mb-4">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl mb-4"
            style={{ backgroundColor: `${category.color}20` }}
          >
            {getIconComponent(category.icon)}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-3">
          <h3 className="text-xl font-semibold text-gray-900 group-hover:text-primary transition-colors duration-200">
            {category.name}
          </h3>
          
          {category.description && (
            <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
              {category.description}
            </p>
          )}
          
          {/* Property Count */}
          {category.property_count !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {category.property_count} {category.property_count === 1 ? 'Property' : 'Properties'}
              </span>
              <div className="flex items-center text-primary group-hover:translate-x-1 transition-transform duration-200">
                <span className="text-sm font-medium mr-1">Explore</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Hover effect overlay */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none"
        style={{ backgroundColor: category.color }}
      ></div>
    </Link>
  );
};